import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { User } from '../types';
import { 
  fetchAdminUsersPaginated, 
  getAdminUserCount, 
  PaginatedAdminUsersResult,
  searchAdminUsers
} from '../services/adminUserService';

interface UsePaginatedAdminUsersOptions {
  pageSize?: number;
  enabled?: boolean;
}

interface SearchAdminUsersResult {
  users: User[];
  total: number;
}

interface UsePaginatedAdminUsersResult {
  users: User[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedRole: string;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedRole: (role: string) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateUserInCache: (updatedUser: User) => void;
  removeUserFromCache: (userId: string) => void;
  invalidateCache: () => void;
}

/**
 * Hook for paginated admin user fetching with search and filtering
 * Matches the customer hook patterns with modes and combined state
 */
export const usePaginatedAdminUsers = ({ 
  pageSize = 20,
  enabled = true 
}: UsePaginatedAdminUsersOptions = {}): UsePaginatedAdminUsersResult => {
  // Server users state (for browse mode)
  const [serverUsers, setServerUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<string, SearchAdminUsersResult>>(new Map());
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  
  // Filter state management
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchAdminUsersResult>>(new Map());
  const [filterResults, setFilterResults] = useState<User[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  
  // Combined mode state (when search and filters are active)
  const [combinedResults, setCombinedResults] = useState<User[]>([]);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  
  // Computed state for combined mode
  const isCombinedMode = isSearchMode && isFilterMode;
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('All');
  
  // Final filtered users (what gets displayed)
  const [users, setUsers] = useState<User[]>([]);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all users for search (with caching)
  const fetchUsersForSearch = useCallback(async (): Promise<User[]> => {
    const cacheKey = 'search_all';
    
    // Check cache first
    const cached = searchCache.get(cacheKey);
    if (cached) {
      console.log('Using cached search data for admin users');
      return cached.users;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      const result = await searchAdminUsers('');
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.users;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch admin users for search';
      setError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [searchCache]);

  // Fetch all users for filtering (with caching)
  const fetchUsersForFilter = useCallback(async (): Promise<User[]> => {
    const cacheKey = 'filter_all';
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log('Using cached filter data for admin users');
      return cached.users;
    }

    try {
      setFilterLoading(true);
      setError(null);
      
      const result = await searchAdminUsers('');
      
      // Cache the result
      setFilterCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.users;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch admin users for filtering';
      setError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  }, [filterCache]);

  // Perform client-side search
  const performSearch = useCallback((query: string, users: User[]): User[] => {
    if (!query.trim()) return users;
    
    const searchTerm = query.toLowerCase();
    return users.filter(user => {
      // Search by name and email
      return user.name.toLowerCase().includes(searchTerm) ||
             user.email.toLowerCase().includes(searchTerm);
    });
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useCallback((): boolean => {
    return selectedRole !== 'All';
  }, [selectedRole]);

  // Apply client-side filters to users
  const applyClientFilters = useCallback((users: User[]): User[] => {
    let result = [...users];

    // Filter by role
    if (selectedRole !== 'All') {
      result = result.filter(user => user.role === selectedRole);
    }

    return result;
  }, [selectedRole]);

  // Debounced filter handler
  const debouncedFilter = useDebouncedCallback(async () => {
    if (!hasActiveFilters()) {
      // No filters active - exit filter mode
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search is active and no server users, load the current page
      if (!isSearchMode && serverUsers.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter filter mode
    setIsFilterMode(true);
    setFilterCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode) {
      // Search + Filter: Apply filters to search results
      const results = applyClientFilters(searchResults);
      setCombinedResults(results);
    } else {
      // Filter only: Fetch all users and apply filters
      const allUsers = await fetchUsersForFilter();
      const results = applyClientFilters(allUsers);
      setFilterResults(results);
    }
  }, 300);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      // Empty search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no filters are active and no server users, load the current page
      if (!hasActiveFilters() && serverUsers.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter search mode and fetch all users if needed
    setIsSearchMode(true);
    setSearchCurrentPage(1);
    setCombinedCurrentPage(1);
    
    const allUsers = await fetchUsersForSearch();
    const results = performSearch(query, allUsers);
    
    setSearchResults(results);
    
    // Apply filters if active
    if (hasActiveFilters()) {
      // Search + Filter
      const combinedResults = applyClientFilters(results);
      setCombinedResults(combinedResults);
    }
  }, 300);

  // Use effect to trigger filter when role selection changes
  useEffect(() => {
    debouncedFilter();
  }, [selectedRole, debouncedFilter]);

  // Load total count
  const loadTotalCount = useCallback(async () => {
    try {
      const count = await getAdminUserCount();
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching admin user count:', err);
      setError('Failed to load admin user count');
    }
  }, []);

  // Load users for a specific page (browse mode only)
  const loadPage = useCallback(async (page: number) => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Regular pagination - no search query
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page > 1) {
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor, we need to fetch from the beginning
        if (!cursor) {
          // Reset and fetch sequentially
          pageCursorsRef.current.clear();
          let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
          
          for (let p = 1; p < page; p++) {
            const result = await fetchAdminUsersPaginated(
              pageSize,
              currentCursor
            );
            
            if (result.lastDoc) {
              pageCursorsRef.current.set(p, result.lastDoc);
              currentCursor = result.lastDoc;
            } else {
              break;
            }
          }
          
          cursor = currentCursor;
        }
      }
      
      const result = await fetchAdminUsersPaginated(pageSize, cursor);
      
      // Store the cursor for this page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
      setServerUsers(result.users);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load admin users';
      setError(errorMessage);
      console.error('Error loading admin users:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize]);

  // Update displayed users based on current mode
  useEffect(() => {
    if (isCombinedMode) {
      // Show combined search + filter results
      setUsers(combinedResults);
    } else if (isSearchMode) {
      // Show search results
      setUsers(searchResults);
    } else if (isFilterMode) {
      // Show filter results
      setUsers(filterResults);
    } else {
      // Show server users (browse mode)
      setUsers(serverUsers);
    }
  }, [isCombinedMode, isSearchMode, isFilterMode, searchResults, filterResults, combinedResults, serverUsers]);

  // Fetch next page
  const fetchNextPage = useCallback(async () => {
    if (hasMore && !loading) {
      await loadPage(currentPage + 1);
    }
  }, [hasMore, loading, currentPage, loadPage]);

  // Fetch specific page
  const fetchPage = useCallback(async (page: number) => {
    if (page >= 1 && page <= totalPages) {
      await loadPage(page);
    }
  }, [totalPages, loadPage]);

  // Handle search query changes
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  // Refresh current data
  const refresh = useCallback(async () => {
    // Clear caches
    setSearchCache(new Map());
    setFilterCache(new Map());
    
    // Clear cursors
    pageCursorsRef.current.clear();
    
    // Reload current page and count
    await Promise.all([
      loadPage(currentPage),
      loadTotalCount()
    ]);
  }, [currentPage, loadPage, loadTotalCount]);

  // Reset to initial state
  const reset = useCallback(() => {
    setSearchQuery('');
    setSelectedRole('All');
    setIsSearchMode(false);
    setIsFilterMode(false);
    setSearchResults([]);
    setFilterResults([]);
    setCombinedResults([]);
    setSearchCache(new Map());
    setFilterCache(new Map());
    pageCursorsRef.current.clear();
    setCurrentPage(1);
    loadPage(1);
  }, [loadPage]);

  // Update user in cache
  const updateUserInCache = useCallback((updatedUser: User) => {
    const updateUserInArray = (users: User[]) => 
      users.map(user => user.id === updatedUser.id ? updatedUser : user);
    
    const updateCacheMap = (cacheMap: Map<string, SearchAdminUsersResult>) => {
      const newCacheMap = new Map(cacheMap);
      for (const [key, value] of newCacheMap.entries()) {
        newCacheMap.set(key, {
          ...value,
          users: updateUserInArray(value.users)
        });
      }
      return newCacheMap;
    };
    
    // Update all user arrays
    setServerUsers(updateUserInArray);
    setSearchResults(updateUserInArray);
    setFilterResults(updateUserInArray);
    setCombinedResults(updateUserInArray);
    
    // Update cache maps
    setSearchCache(updateCacheMap);
    setFilterCache(updateCacheMap);
  }, []);

  // Remove user from cache
  const removeUserFromCache = useCallback((userId: string) => {
    const removeUserFromArray = (users: User[]) => 
      users.filter(user => user.id !== userId);
    
    const removeFromCacheMap = (cacheMap: Map<string, SearchAdminUsersResult>) => {
      const newCacheMap = new Map(cacheMap);
      for (const [key, value] of newCacheMap.entries()) {
        newCacheMap.set(key, {
          ...value,
          users: removeUserFromArray(value.users),
          total: value.total - 1
        });
      }
      return newCacheMap;
    };
    
    // Remove from all user arrays
    setServerUsers(removeUserFromArray);
    setSearchResults(removeUserFromArray);
    setFilterResults(removeUserFromArray);
    setCombinedResults(removeUserFromArray);
    
    // Remove from cache maps
    setSearchCache(removeFromCacheMap);
    setFilterCache(removeFromCacheMap);
    
    // Update total count
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  // Invalidate all caches
  const invalidateCache = useCallback(() => {
    setSearchCache(new Map());
    setFilterCache(new Map());
    pageCursorsRef.current.clear();
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (enabled) {
      const initialize = async () => {
        await Promise.all([
          loadPage(1),
          loadTotalCount()
        ]);
      };
      
      initialize();
    }
  }, [enabled, loadPage, loadTotalCount]);

  return {
    users,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    searchQuery,
    selectedRole,
    isSearchMode,
    isFilterMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    setSearchQuery,
    setSelectedRole,
    handleSearch,
    fetchNextPage,
    fetchPage,
    refresh,
    reset,
    updateUserInCache,
    removeUserFromCache,
    invalidateCache,
  };
}; 